import useLoadingAction from 'hooks/use-loading-action';
import { noop } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import passwordFormat from 'components/forms/validators/rules/rule-password-format';
import { useForm } from '@mantine/form';
import { useEffect, useMemo } from 'react';
import { useDisclosure } from '@mantine/hooks';
import FormInputGroup from 'components/forms/FormInputGroup';
import { Box, Group, PasswordInput, SimpleGrid, Stack } from '@mantine/core';
import Toast from 'components/Toast';
import { IconPencil } from '@tabler/icons-react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';

/**
 * The data collected from the form.
 */
export interface AdminChangePasswordFormData {
  newPassword: string;
  newPasswordRepeat: string;
}

/**
 * The form for changing other user's password.
 */
export default function AdminChangePasswordForm({
  onSubmit = noop,
  message,
}: FormProps<AdminChangePasswordFormData> = {}) {
  const [opened, { open, close }] = useDisclosure(false);
  const [{ loading }, submitWithLoading] = useLoadingAction(onSubmit);
  const { isTablet } = useResponsiveDesign();

  const form = useForm<AdminChangePasswordFormData>({
    initialValues: {
      newPassword: '',
      newPasswordRepeat: '',
    },
    validate: {
      newPassword: createValidator([required, passwordFormat]),
      newPasswordRepeat: (newPasswordRepeat, { newPassword }) => {
        if (!newPasswordRepeat) {
          return 'Pole je povinné';
        }

        if (newPasswordRepeat !== newPassword) {
          return 'Heslá sa nezhodujú';
        }
      },
    },
  });

  const submit = useMemo(() => form.onSubmit(submitWithLoading), [form, submitWithLoading]);

  /**
   * Clear errors when the form is closed.
   */
  useEffect(() => {
    if (!opened) {
      form.clearErrors();
    }
  }, [opened]);

  const success = message?.type === 'success';

  // This is a hack to prevent the form from being submitted twice.
  useEffect(() => {
    if (success) {
      form.reset();
      close();
    }
  }, [success]);

  return (
    <FormInputGroup groupTitle="Heslo">
      <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
        <Stack spacing={24}>
          <Box hidden={!opened}>
            <Stack spacing={24}>
              <PasswordInput
                label="Zadajte nové heslo"
                placeholder="Nové heslo"
                size="lg"
                form="admin-change-password-form"
                name="objekto-newPassword"
                {...form.getInputProps('newPassword')}
              />
              <PasswordInput
                label="Zopakujte nové heslo znova"
                placeholder="Nové heslo znova"
                size="lg"
                form="admin-change-password-form"
                name="objekto-newPasswordRepeat"
                {...form.getInputProps('newPasswordRepeat')}
              />
            </Stack>
          </Box>

          {message && <Toast {...message} opened withCloseButton={false} fullWidth />}

          {!success && (
            <Group spacing={24} position="left">
              <Button
                type="button"
                variant={opened ? 'primary' : 'subtle-compact'}
                leftIcon={<IconPencil stroke="1.5" width={24} height={24} />}
                onClick={() => (opened ? submit() : open())}
                loading={loading}
              >
                Zmeniť heslo
              </Button>
              {opened && (
                <Button onClick={close} variant="subtle-compact" disabled={loading}>
                  Zrušiť
                </Button>
              )}
            </Group>
          )}
        </Stack>
      </SimpleGrid>
    </FormInputGroup>
  );
}
